import React, { useState, useEffect } from 'react';
import { useNotification } from '../../../context/NotificationContext';
import { adminAPI } from '../../../utils/api';

const FoodLinks = () => {

  const { showSuccess, showError } = useNotification();
  const [foodLinks, setFoodLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    url: '',
    platform: 'other',
    image_url: '',
    is_active: true
  });

  useEffect(() => {
    fetchFoodLinks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFoodLinks = async () => {
    try {
      const response = await adminAPI.getAllFoodLinks();
      if (response.data.success) {
        setFoodLinks(response.data.data || []);
      }
    } catch (error) {
      showError('Failed to load food links');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLink) {
        await adminAPI.updateFoodLink(editingLink._id, formData);
        showSuccess('Food link updated successfully');
      } else {
        await adminAPI.createFoodLink(formData);
        showSuccess('Food link created successfully');
      }
      setShowForm(false);
      setEditingLink(null);
      setFormData({
        title: '',
        description: '',
        url: '',
        platform: 'other',
        image_url: '',
        is_active: true
      });
      fetchFoodLinks();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to save food link');
    }
  };

  const handleEdit = (link) => {
    setEditingLink(link);
    setFormData({
      title: link.title,
      description: link.description || '',
      url: link.url,
      platform: link.platform || 'other',
      image_url: link.image_url || '',
      is_active: link.is_active
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this food link?')) return;
    try {
      await adminAPI.deleteFoodLink(id);
      showSuccess('Food link deleted successfully');
      fetchFoodLinks();
    } catch (error) {
      showError('Failed to delete food link');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading food links...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gold">Manage Food Links</h1>
        <button
          onClick={() => {
            setShowForm(!showForm);
            setEditingLink(null);
            setFormData({
              title: '',
              description: '',
              url: '',
              platform: 'other',
              image_url: '',
              is_active: true
            });
          }}
          className="bg-gold text-dark px-4 py-2 rounded-lg font-medium"
        >
          {showForm ? 'Cancel' : 'Add New Link'}
        </button>
      </div>

      {showForm && (
        <div className="bg-gray-800 rounded-xl p-6">
          <h2 className="text-xl font-semibold mb-4 text-gold">
            {editingLink ? 'Edit Food Link' : 'Create New Food Link'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                className="w-full bg-gray-700 text-light rounded-lg px-4 py-2 border border-gray-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full bg-gray-700 text-light rounded-lg px-4 py-2 border border-gray-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">URL (Link) *</label>
              <input
                type="url"
                name="url"
                value={formData.url}
                onChange={handleInputChange}
                required
                className="w-full bg-gray-700 text-light rounded-lg px-4 py-2 border border-gray-600"
                placeholder="https://swiggy.com or https://zomato.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Platform</label>
              <select
                name="platform"
                value={formData.platform}
                onChange={handleInputChange}
                className="w-full bg-gray-700 text-light rounded-lg px-4 py-2 border border-gray-600"
              >
                <option value="swiggy">Swiggy</option>
                <option value="zomato">Zomato</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Image URL</label>
              <input
                type="url"
                name="image_url"
                value={formData.image_url}
                onChange={handleInputChange}
                className="w-full bg-gray-700 text-light rounded-lg px-4 py-2 border border-gray-600"
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
                className="mr-2"
              />
              <label className="text-sm">Active</label>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="bg-gold text-dark px-4 py-2 rounded-lg font-medium"
              >
                {editingLink ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingLink(null);
                }}
                className="bg-gray-700 text-light px-4 py-2 rounded-lg font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-4">
        {foodLinks.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <p>No food links found. Create your first link above.</p>
          </div>
        ) : (
          foodLinks.map((link) => (
            <div key={link._id} className="bg-gray-800 rounded-xl p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-semibold">{link.title}</h3>
                    <span className={`px-2 py-1 rounded text-xs ${link.is_active ? 'bg-green-600' : 'bg-gray-600'}`}>
                      {link.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <span className="px-2 py-1 rounded text-xs bg-gray-600 capitalize">{link.platform}</span>
                  </div>
                  {link.description && (
                    <p className="text-gray-400 mb-2">{link.description}</p>
                  )}
                  <div className="space-y-1 text-sm text-gray-400">
                    <p>URL: <span className="text-gold break-all">{link.url}</span></p>
                    {link.image_url && (
                      <p>Image: <span className="text-gold break-all">{link.image_url}</span></p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => handleEdit(link)}
                    className="bg-blue-600 text-white px-3 py-1 rounded text-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(link._id)}
                    className="bg-red-600 text-white px-3 py-1 rounded text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default FoodLinks;
